import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import TextareaAutosize  from '@mui/material/TextareaAutosize';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import { tTest } from '../lib/stats'
import toFixed from '../lib/toFixed'

function TTest() {
  const [groupOneLabel, setGroupOneLabel] = useAutoSaveState('groupOneLabel', 'Group One')
  const [groupTwoLabel, setGroupTwoLabel] = useAutoSaveState('groupTwoLabel', 'Group Two')
  const [data1, setData1] = useAutoSaveState('data1', '')
  const [data2, setData2] = useAutoSaveState('data2', '')
  const [tails, setTails] = useAutoSaveState('tails', 2)
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [result, setResult] = useState({})
  const { p, t, mean1, mean2, sd1, sd2 } = result

  const calculate = () => {
    const input1 = data1.split(/[^0-9.-]/).filter(v => v).map(v => parseFloat(v))
    const input2 = data2.split(/[^0-9.-]/).filter(v => v).map(v => parseFloat(v))
    const tail = parseInt(tails)
    const output = tTest(input1, input2, tail)
    setResult(output)
    setPreview(`p: ${toFixed(output.p, 3)}, t: ${toFixed(output.t, 3)}`)
    window.gtag('event', 'calculate-t-test')
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            T Test
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="groupOneLabel"
                  fullWidth
                  id="groupOneLabel"
                  label="Group One"
                  autoFocus
                  value={groupOneLabel}
                  onChange={e => setGroupOneLabel(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">Values</Typography>
                <TextareaAutosize
                  style={{ width: '100%' }}
                  aria-label="data"
                  minRows={3}
                  maxRows={20}
                  placeholder="Enter data..."
                  value={data1}
                  onChange={e => setData1(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="groupTwoLabel"
                  fullWidth
                  id="groupTwoLabel"
                  label="Group Two"
                  value={groupTwoLabel}
                  onChange={e => setGroupTwoLabel(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">Values</Typography>
                <TextareaAutosize
                  style={{ width: '100%' }}
                  aria-label="data"
                  minRows={3}
                  maxRows={20}
                  placeholder="Enter data..."
                  value={data2}
                  onChange={e => setData2(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={tails}
                onChange={e => setTails(e.target.value)}
              >
                <FormControlLabel value="2" control={<Radio />} label="Two Tailed" />
                <FormControlLabel value="1" control={<Radio />} label="One Tailed" />
              </RadioGroup>
            </FormControl>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>
        {result.p && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h5" gutterBottom>
                <EqualizerIcon /> T-Test Results
              </Typography>
            </Grid>
            <Divider style={{ marginBottom: '20px' }} />
            <Typography
              variant="body1"
              sx={{
                color: p < 0.05 ? 'green' : 'red',
                textAlign: 'center',
                fontWeight: 'bold'
              }}
            >
              {p < 0.05 ? 'Significant Results (p < 0.05)' : 'Results Not Significant (p >= 0.05)'}
            </Typography>
            <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>P-Value:</b> {toFixed(p, 3)}</Typography>
              <Typography variant="body1"><b>T-Value:</b> {toFixed(t, 3)}</Typography>
            </Box>

            <Divider style={{ marginBottom: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>{groupOneLabel} Mean:</b> {toFixed(mean1, 5)}</Typography>
              <Typography variant="body1"><b>{groupTwoLabel} Mean:</b> {toFixed(mean2, 5)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>{groupOneLabel} Standard Deviation:</b> {toFixed(sd1, 5)}</Typography>
              <Typography variant="body1"><b>{groupTwoLabel} Standard Deviation:</b> {toFixed(sd2, 5)}</Typography>
            </Box>
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  );
}

export default function WrappedTTest() {
  return (
    <AutoSaveProvider>
      <TTest />
    </AutoSaveProvider>
  )
}
